import { useState, useEffect, useRef, useCallback } from 'react';
import { clearAllCache } from '../services/cache';
import {
  fetchDailyTrend,
  fetchTopQueries,
  fetchTopPages,
  fetchDeviceBreakdown,
  fetchCountryBreakdown,
} from '../services/gsc';
import type {
  GSCDateRange,
  GSCSummary,
  GSCQueryRow,
  GSCPageRow,
  GSCDateRow,
  GSCDeviceRow,
  GSCCountryRow,
} from '../types/searchConsole';

interface UseSearchConsoleResult {
  summary: GSCSummary | null;
  queries: GSCQueryRow[] | null;
  pages: GSCPageRow[] | null;
  dailyTrend: GSCDateRow[] | null;
  devices: GSCDeviceRow[] | null;
  countries: GSCCountryRow[] | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useSearchConsole(dateRange: GSCDateRange): UseSearchConsoleResult {
  const [summary, setSummary] = useState<GSCSummary | null>(null);
  const [queries, setQueries] = useState<GSCQueryRow[] | null>(null);
  const [pages, setPages] = useState<GSCPageRow[] | null>(null);
  const [dailyTrend, setDailyTrend] = useState<GSCDateRow[] | null>(null);
  const [devices, setDevices] = useState<GSCDeviceRow[] | null>(null);
  const [countries, setCountries] = useState<GSCCountryRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => {
    clearAllCache();
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    let cancelled = false;

    async function loadAll() {
      setLoading(true);
      setError(null);

      const results = await Promise.allSettled([
        fetchDailyTrend(dateRange, controller.signal),
        fetchTopQueries(dateRange, controller.signal),
        fetchTopPages(dateRange, controller.signal),
        fetchDeviceBreakdown(dateRange, controller.signal),
        fetchCountryBreakdown(dateRange, controller.signal),
      ]);

      if (cancelled) return;

      const [trendRes, queriesRes, pagesRes, devicesRes, countriesRes] = results;

      if (trendRes.status === 'fulfilled') {
        setDailyTrend(trendRes.value.rows);
        setSummary(trendRes.value.summary);
      }
      if (queriesRes.status === 'fulfilled') setQueries(queriesRes.value);
      if (pagesRes.status === 'fulfilled') setPages(pagesRes.value);
      if (devicesRes.status === 'fulfilled') setDevices(devicesRes.value);
      if (countriesRes.status === 'fulfilled') setCountries(countriesRes.value);

      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length === results.length) {
        const firstErr = (failures[0] as PromiseRejectedResult).reason;
        setError(firstErr instanceof Error ? firstErr.message : 'Failed to load Search Console data');
      }

      setLoading(false);
    }

    loadAll();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [dateRange, refreshKey]);

  return { summary, queries, pages, dailyTrend, devices, countries, loading, error, refetch };
}
